'use client';

// Modified from vaul 1.1.2 (MIT); see THIRD_PARTY_NOTICES.md. Trimmed to the Dialog and animation behaviour Spellbook's Drawer uses.
import { useControllableState } from '@magicschool/spellbook/hooks/useControllableState';
import * as DialogPrimitive from 'radix-ui/dialog';
import { type ComponentPropsWithoutRef, createContext, type ReactNode, type Ref, useContext, useRef } from 'react';

export type DrawerDirection = 'top' | 'bottom' | 'left' | 'right';

export type DrawerRootProps = {
  children?: ReactNode;
  open?: boolean;
  /** Opened by default, skips the initial enter animation. Still reacts to `open` state changes. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Fires when the user dismisses the drawer (Escape, outside click, `Drawer.Close`), before `onOpenChange(false)`. Not called when the parent sets `open` to `false`. */
  onClose?: () => void;
  /** When `false`, Escape, outside clicks and `Drawer.Close` do not close the drawer; drive it with `open`. @default true */
  dismissible?: boolean;
  /** When `false`, focus stays where it was when the drawer opens. @default true */
  autoFocus?: boolean;
};

type DrawerContextValue = {
  animate: boolean;
  autoFocus: boolean;
};

const DrawerContext = createContext<DrawerContextValue | null>(null);

const useDrawerContext = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('Drawer primitives must be used within a Drawer root');
  }
  return context;
};

export const Root = ({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  onClose,
  dismissible = true,
  autoFocus = true,
  children,
}: DrawerRootProps) => {
  const [isOpen, setIsOpen] = useControllableState<boolean>({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });

  // A defaultOpen drawer skips its enter animation until it first closes; after that it animates like any other.
  const skipEnterAnimation = useRef(defaultOpen);
  if (!isOpen) skipEnterAnimation.current = false;

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!dismissible && !open) return;
        if (!open) onClose?.();
        setIsOpen(open);
      }}
    >
      <DrawerContext.Provider value={{ animate: !skipEnterAnimation.current, autoFocus }}>{children}</DrawerContext.Provider>
    </DialogPrimitive.Root>
  );
};

type OverlayProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & { ref?: Ref<HTMLDivElement> };

export const Overlay = (props: OverlayProps) => {
  const { animate } = useDrawerContext();
  return <DialogPrimitive.Overlay data-drawer-overlay="" data-drawer-animate={animate ? 'true' : 'false'} {...props} />;
};

export type DrawerPrimitiveContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { ref?: Ref<HTMLDivElement> };

export const Content = ({ onOpenAutoFocus, ...rest }: DrawerPrimitiveContentProps) => {
  const { animate, autoFocus } = useDrawerContext();

  return (
    <DialogPrimitive.Content
      data-drawer=""
      data-drawer-animate={animate ? 'true' : 'false'}
      {...rest}
      onOpenAutoFocus={(e) => {
        onOpenAutoFocus?.(e);
        if (!autoFocus) {
          e.preventDefault();
        }
      }}
    />
  );
};

export const { Portal, Trigger, Close, Title } = DialogPrimitive;
